import { FC } from "react";
import { Avatar, Box, Flex, Icon, Menu, MenuButton, MenuDivider, MenuGroup, MenuItem, MenuList, useDisclosure } from "@chakra-ui/react";
import { IconChevronDown, IconLogout2, IconUserCircle } from "@tabler/icons-react";
import { useMsal } from "@azure/msal-react";
import { useMatch } from "react-router-dom";
import { pageTitleAtom } from "../../atoms";
import { useAtomValue } from "jotai";
import { ApplicationLayoutBoxShadow, TopNav as CoreTopNav } from "@am-tax/fe-core";
import { useClient } from "../../api";
import { useHasAccess } from "../../hooks/useHasAccess";
import { ProfileModal } from "../../pages/ProfilePage";

const AVATAR_COLORS = ["blue", "teal", "green", "purple", "orange", "cyan", "pink", "red"];

function getAvatarColor(name: string): string {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export const TopNav: FC = () => {
    const { instance } = useMsal();
    const { identity } = useHasAccess();
    const pageTitle = useAtomValue(pageTitleAtom);
    const match = useMatch("/clients/:cId/*");
    let clientId = undefined;
    if (match) {
        const { cId } = match.params;
        clientId = cId;
    }
    const clientQuery = useClient(clientId);
    const profileModal = useDisclosure();

    const logout = () => {
        sessionStorage.clear();
        instance.logoutRedirect({ postLogoutRedirectUri: "/" });
    };

    const displayName = identity ? `${identity.givenname ?? ""} ${identity.surname ?? ""}`.trim() : undefined;
    const email = identity?.mail;
    const avatarColor = getAvatarColor(displayName || "");

    return (
        <>
            <CoreTopNav
                // @ts-ignore
                title={pageTitle}
                subTitle={clientQuery.data?.name}
                menu={
                    <Menu>
                        <MenuButton
                            as={Flex}
                            cursor="pointer"
                            alignItems="center"
                            alignSelf="center"
                            gap={1.5}
                            px={2}
                            py={1}
                            borderRadius="lg"
                            border="1px solid"
                            borderColor="gray.200"
                            _hover={{ bg: "gray.50", borderColor: "gray.300" }}
                            transition="all 0.15s"
                        >
                            <Avatar size="sm" name={displayName} bg={`${avatarColor}.400`} color="white" fontWeight="semibold" />
                            <Icon as={IconChevronDown} boxSize={3.5} color="gray.500" />
                        </MenuButton>
                        <MenuList boxShadow={ApplicationLayoutBoxShadow} minW="220px">
                            {displayName && (
                                <>
                                    <MenuGroup>
                                        <Box px={3} py={1}>
                                            <Box fontSize="sm" fontWeight="semibold" color="gray.700" noOfLines={1}>
                                                {displayName}
                                            </Box>
                                            {email && (
                                                <Box fontSize="xs" color="gray.400" noOfLines={1}>
                                                    {email}
                                                </Box>
                                            )}
                                        </Box>
                                    </MenuGroup>
                                    <MenuDivider />
                                </>
                            )}
                            <MenuItem onClick={profileModal.onOpen} icon={<IconUserCircle size="1rem" />}>
                                Profile
                            </MenuItem>
                            <MenuDivider />
                            <MenuItem onClick={logout} icon={<IconLogout2 size="1rem" />} color="red.500">
                                Sign out
                            </MenuItem>
                        </MenuList>
                    </Menu>
                }
            />
            <ProfileModal isOpen={profileModal.isOpen} onClose={profileModal.onClose} />
        </>
    );
};
